import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton,
    Typography, Box, Button, TextField, Stack, CircularProgress, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { setupInterceptorsTo } from "../Componentes/API/AxiosBuilder";
import axios from 'axios';
import { URL_API_SIA, ACCESO_ELIMINAR_CHATBOT, ACCESO_MODIFICAR_CHATBOT } from "../Contantes/Constantes";
import { useAuth } from "../Auth/State/AuthContext";
import { TreeView, TreeItem } from '@mui/x-tree-view';

const buildIncidenciaTree = (incidencias) => {
    const map = new Map();
    const raiz = [];

    incidencias.forEach((i) => {
        map.set(i.id, { ...i, hijos: [] });
    });

    map.forEach((item) => {
        if (item.id_padre && map.has(item.id_padre)) {
            map.get(item.id_padre).hijos.push(item);
        } else {
            raiz.push(item);
        }
    });

    return raiz;
};

const findExpandedAncestor = (id, map) => {
    const path = [];
    while (id && map.has(id)) {
        const item = map.get(id);
        if (!item || !item.id_padre) break;
        path.push(String(item.id_padre));
        id = item.id_padre;
    }
    return path.reverse();
};

const renderTreeItem = (inc, handleSelect) => (
    <TreeItem
        nodeId={String(inc.id)}
        key={inc.id}
        label={
            <div
                onDoubleClick={(e) => {
                    e.stopPropagation(); // evita expansión automática
                    handleSelect(inc);   // selecciona incidencia
                }}
                onClick={(e) => {
                    e.stopPropagation(); // evita selección no deseada
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
            >
                {inc.contenido || `Incidencia ${inc.id}`}
            </div>
        }
    >
        {inc.hijos && inc.hijos.map((h) => renderTreeItem(h, handleSelect))}
    </TreeItem>
);



const ModalIncidencias = ({ open, onClose, incidenciasList, refreshIncidencias }) => {
    const [selectedIncidencia, setSelectedIncidencia] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState([]);

    const axiosInstance = setupInterceptorsTo(axios.create());
    const { hasRole } = useAuth();

    useEffect(() => {
        if (isEditing && selectedIncidencia?.id_padre) {
            const map = new Map();
            incidenciasList.forEach((i) => {
                map.set(i.id, { ...i });
            });

            const newAncestors = findExpandedAncestor(selectedIncidencia.id_padre, map);

            setExpanded((prev) => {
                const set = new Set(prev);
                newAncestors.forEach((id) => set.add(id));
                return Array.from(set);
            });
        }
    }, [isEditing, selectedIncidencia?.id_padre]);

    const handleSelect = (inc) => {
        setSelectedIncidencia(inc);
        setIsEditing(false);
        setFormData({ contenido: inc.contenido }); // No se incluye id_padre
    };

    const handleBack = () => {
        setSelectedIncidencia(null);
        setIsEditing(false);
        setFormData({});
        setExpanded([]);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.delete(`${URL_API_SIA}/chatbot/incidencia/${selectedIncidencia.id}`);
            if (response.data.success && typeof refreshIncidencias === 'function') {
                await refreshIncidencias();
            }
            handleBack();
        } catch (err) {
            console.error('Error al eliminar incidencia:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => setIsEditing(true);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleUpdate = async () => {
        if (!formData.contenido?.trim()) return;
        setLoading(true);
        try {
            const dataToSend = {
                id: selectedIncidencia.id,
                contenido: formData.contenido.trim(),
                // id_padre NO se incluye más
            };

            const response = await axiosInstance.put(`${URL_API_SIA}/chatbot/incidencia`, JSON.stringify(dataToSend));
            if (response.data.success && typeof refreshIncidencias === 'function') {
                await refreshIncidencias();
            }
            handleBack();
        } catch (err) {
            console.error('Error al actualizar incidencia:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => { handleBack(); onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>
                {selectedIncidencia ? (
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={handleBack} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" component="p">
                            {isEditing ? 'Editar Incidencia' : 'Detalle de Incidencia'}
                        </Typography>
                    </Box>
                ) : (
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h5" component="p">Incidencias</Typography>
                        <IconButton onClick={() => { handleBack(); onClose(); }} edge="end">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                )}
            </DialogTitle>

            <Divider sx={{ width: '92%', mx: 'auto' }} />

            <DialogContent>
                {!selectedIncidencia ? (
                    <Box
                        sx={{
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            padding: 1,
                            maxHeight: 400,
                            overflowY: 'auto',
                        }}
                    >
                        <TreeView
                            expanded={expanded}
                            onNodeToggle={(e, ids) => setExpanded(ids)}
                            defaultExpandIcon={<span>▶</span>}
                            defaultCollapseIcon={<span>▼</span>}
                        >
                            {buildIncidenciaTree(incidenciasList).map((inc) =>
                                renderTreeItem(inc, handleSelect)
                            )}
                        </TreeView>
                    </Box>

                ) : (
                    <>
                        {isEditing ? (
                            <Box pt={1}>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Contenido"
                                        value={formData.contenido || ''}
                                        onChange={(e) => handleChange('contenido', e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleUpdate}
                                        disabled={loading || !formData.contenido?.trim()}
                                        startIcon={loading && <CircularProgress size={20} color="inherit" />}
                                    >
                                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                                    </Button>
                                </Stack>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body1" gutterBottom>
                                    <strong>Contenido:</strong> {selectedIncidencia.contenido}
                                </Typography>
                                {selectedIncidencia.id_padre && (
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Padre:</strong> {
                                        incidenciasList.find((inc) => inc.id === selectedIncidencia.id_padre)?.contenido || selectedIncidencia.id_padre
                                    }
                                    </Typography>
                                )}

                                {loading ? (
                                    <Box mt={4} display="flex" justifyContent="center" alignItems="center" height="100px">
                                        <CircularProgress size={32} />
                                    </Box>
                                ) : (
                                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                                        {hasRole(ACCESO_ELIMINAR_CHATBOT) && (
                                            <Button variant="outlined" color="error" onClick={handleDelete} disabled={loading}>
                                                Eliminar
                                            </Button>
                                        )}
                                        {hasRole(ACCESO_MODIFICAR_CHATBOT) && (
                                            <Button variant="contained" onClick={handleEdit}>
                                                Editar
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ModalIncidencias;
